/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { isMac } from "./DeviceUtils";

export const mouseButtons = Object.freeze({
  NONE: 0,
  LEFT: 1,
  RIGHT: 2,
  MIDDLE: 3
});

export const findPressedMouseButton = evt => {
  let pressedButton = mouseButtons.NONE;
  if (
    (evt.buttons === undefined || evt.buttons === 0) &&
    evt.which === undefined
  ) {
    if (evt.button === 0) {
      pressedButton = mouseButtons.LEFT;
    } else if (evt.button === 2) {
      pressedButton = mouseButtons.RIGHT;
    } else if (evt.button === 1) {
      pressedButton = mouseButtons.MIDDLE;
    }
  } else if (
    (evt.buttons === undefined && evt.which === 1) ||
    evt.buttons === 1
  ) {
    pressedButton = mouseButtons.LEFT;
  } else if (
    (evt.buttons === undefined && evt.which === 3) ||
    evt.buttons === 2
  ) {
    pressedButton = mouseButtons.RIGHT;
  } else if (
    (evt.buttons === undefined && evt.which === 2) ||
    evt.buttons === 4
  ) {
    pressedButton = mouseButtons.MIDDLE;
  }

  return pressedButton;
};

// Function to determine which mouse button was clicked
// Returns: NONE (0) if no button is pressed, LEFT (1), RIGHT (2), MIDDLE (3) otherwise
export const findPressedMouseButtonUpdated = evt => {
  // Initialize pressedButton to NONE (0)
  let pressedButton = mouseButtons.NONE;

  // Check if evt.buttons is supported (modern browsers)
  if (evt.buttons !== undefined) {
    // Check if the first bit (left mouse button) is set in evt.buttons
    if (evt.buttons & 1) {
      pressedButton = mouseButtons.LEFT; // Set pressedButton to LEFT (1)
    }
    // Check if the second bit (right mouse button) is set in evt.buttons
    else if (evt.buttons & 2) {
      pressedButton = mouseButtons.RIGHT; // Set pressedButton to RIGHT (2)
    }
    // Check if the third bit (middle mouse button) is set in evt.buttons
    else if (evt.buttons & 4) {
      pressedButton = mouseButtons.MIDDLE; // Set pressedButton to MIDDLE (3)
    }
  }
  // For browsers that don't support evt.buttons (backward compatibility)
  else {
    // Use evt.which to determine the clicked button
    // LEFT button (evt.which = 1) corresponds to the first bit
    // RIGHT button (evt.which = 3) corresponds to the second bit
    // MIDDLE button (evt.which = 2) corresponds to the third bit
    switch (evt.which) {
      case 1: // LEFT button
        pressedButton = mouseButtons.LEFT; // Set pressedButton to LEFT (1)
        break;
      case 3: // RIGHT button
        pressedButton = mouseButtons.RIGHT; // Set pressedButton to RIGHT (2)
        break;
      case 2: // MIDDLE button
        pressedButton = mouseButtons.MIDDLE; // Set pressedButton to MIDDLE (3)
        break;
      default:
        pressedButton = mouseButtons.NONE; // Set pressedButton to NONE (0) if no button is pressed
    }
  }

  return pressedButton; // Return the pressedButton value
};

export const isControlORCommandPressed = event =>
  event.metaKey || (event.ctrlKey && !isMac());

export const getTouchCoordinates = (event, parentElem) => {
  const elementBoundingRects = parentElem.getBoundingClientRect();
  const xInPage = event.center.x - elementBoundingRects.left;
  const yInPage = event.center.y - elementBoundingRects.top;

  return { xInPage, yInPage };
};

export const getTargetParentElement = (event, parentClass) => {
  let targetElem = event.target;
  while (targetElem && !targetElem.classList.contains(parentClass)) {
    targetElem = targetElem.parentElement;
  }
  return targetElem;
};

export const getCoordinatesRelativeToTarget = (evt, target) => {
  let x = evt.pageX;
  let y = evt.pageY;
  if (target) {
    const elementBoundingRects = target.getBoundingClientRect();
    x =
      evt.pageX -
      elementBoundingRects.left -
      document.documentElement.scrollLeft;
    y =
      evt.pageY - elementBoundingRects.top - document.documentElement.scrollTop;
  }
  return { x, y };
};
